import { InputSelect, InputWrapper } from '@vrplatform/components';
import { type gqlV2, useTeam } from '@vrplatform/data';
import { useInfiniteQuery, useQuery } from '@vrplatform/data';
import type { SelectItem } from '@vrplatform/components';
import { isUUID } from '@vrplatform/utils';
import { Box } from '@mantine/core';
import { whereAccounts } from '@vrplatform/ui-common';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
import type { UnreconciledBankRecord } from '../../../reconciliation/bank-accounts/view/UnreconciledBankRecordCard';
import { useExpenseForm } from '../useExpenseForm';

function useAccountQuery(search: string) {
  const [{ id: teamId }] = useTeam();

  return useInfiniteQuery(
    (q, { teamId, search }, { limit, offset }) => {
      const where: gqlV2.account_bool_exp = whereAccounts({
        tenantId: teamId,
        search,
        type: 'bank',
      });

      const list = q
        .accounts({
          where,
          order_by: [
            { bankAccount: { category: 'desc_nulls_last' } },
            { title: 'asc_nulls_last' },
          ],
          limit,
          offset,
        })
        .map<SelectItem>((res) => ({
          value: res.id,
          label: res.title || 'No name',
        }));

      const aggregate = q.accountAggregate({ where }).aggregate?.count() || 0;

      return {
        list,
        aggregate,
      };
    },
    {
      skip: !teamId,
      queryKey: ['listings'],
      variables: {
        teamId,
        search: search?.trim(),
      },
    }
  );
}

const EXTERNAL_ACCOUNT: SelectItem = {
  label: 'Non-Trust Account',
  value: 'non-trust',
};

export const ExpenseBankAccountInput = ({
  label,
  showRequired = false,
  required = true,
  bankRecord,
  disabled,
  clearable = false,
}: {
  label?: string;
  showRequired?: boolean;
  required?: boolean;
  bankRecord?: UnreconciledBankRecord;
  disabled: boolean;
  clearable?: boolean;
}) => {
  const methods = useExpenseForm();
  const [search, setSearch] = useState('');

  const queryData = useAccountQuery(search);

  return (
    <Box>
      <Controller
        control={methods.control}
        name={'accountId'}
        rules={{
          required: required ? 'Please select an account' : false,
        }}
        render={({ field, fieldState }) => {
          const { data, isLoading: loading } = useQuery(
            (q, args) => {
              if (args.value === EXTERNAL_ACCOUNT.value)
                return { account: EXTERNAL_ACCOUNT };

              if (!args.value || !isUUID(args.value))
                return {
                  account: null,
                };

              const account = q
                .accounts({
                  where: {
                    id: { _eq: args.value },
                  },
                })
                .map<SelectItem>((item) => ({
                  label: item.title || 'No name',
                  value: item.id!,
                }))[0];

              return {
                account,
              };
            },
            {
              keepPreviousData: true,
              variables: {
                value: field.value,
              },
            }
          );

          const account = field.value
            ? field.value === 'non-trust'
              ? EXTERNAL_ACCOUNT
              : data?.account || null
            : null;

          return (
            <InputWrapper
              error={fieldState.error?.message}
              label={label}
              required={showRequired}
            >
              <InputSelect
                data-testid={field.name}
                name={field.name}
                type="single"
                value={account}
                setValue={(v) => {
                  if (v?.value) {
                    methods.clearErrors('accountId');
                  }
                  field.onChange(v?.value || null);
                }}
                infiniteData={{ ...queryData, setSearch }}
                inputProps={{
                  error: !!fieldState.error,
                  placeholder: 'Select account',
                  loadingQuery: loading,
                  disabled: disabled || !!bankRecord,
                  withClearButton: clearable,
                }}
                dropdownProps={{
                  width: 'target',
                }}
                customBottomActions={[
                  {
                    id: 'non-trust',
                    label: EXTERNAL_ACCOUNT.label,
                    onSubmit: () => field.onChange(EXTERNAL_ACCOUNT.value),
                    description:
                      'An external account not on your chart of account.',
                  },
                ]}
              />
            </InputWrapper>
          );
        }}
      />
    </Box>
  );
};
